import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DialogSelect } from "@/components/ui/dialog-select";
import { Customer } from "@/types/customer";
import { Plus } from "lucide-react";

interface AddCustomerDialogProps {
  onAdd: (customer: Omit<Customer, "id" | "createdAt">) => void;
  existingCodes: number[];
  jobs: string[];
}

export function AddCustomerDialog({ onAdd, existingCodes, jobs }: AddCustomerDialogProps) {
  const [open, setOpen] = useState(false);
  const [codeError, setCodeError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    customerCode: "",
    fullName: "",
    phone1: "",
    phone2: "",
    guarantorName: "",
    guarantorPhone: "",
    job: "",
    address: "",
    notes: "",
  });

  const jobOptions = jobs.map(job => ({ value: job, label: job }));

  const handleCodeChange = (value: string) => {
    setFormData({ ...formData, customerCode: value });
    const code = parseInt(value);
    if (value && existingCodes.includes(code)) {
      setCodeError("ئەم کۆدە پێشتر بەکارهاتووە");
    } else {
      setCodeError("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const nativeEvent = e.nativeEvent as SubmitEvent;
    if (!nativeEvent.submitter) return;
    
    if (isSubmitting) return;
    
    const code = parseInt(formData.customerCode);
    if (existingCodes.includes(code)) {
      setCodeError("ئەم کۆدە پێشتر بەکارهاتووە");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      onAdd({
        customerCode: code,
        fullName: formData.fullName,
        phone1: formData.phone1,
        phone2: formData.phone2,
        guarantorName: formData.guarantorName,
        guarantorPhone: formData.guarantorPhone,
        job: formData.job,
        address: formData.address,
        notes: formData.notes,
      });
      
      setFormData({
        customerCode: "",
        fullName: "",
        phone1: "",
        phone2: "",
        guarantorName: "",
        guarantorPhone: "",
        job: "",
        address: "",
        notes: "",
      });
      setCodeError("");
      setOpen(false);
    } catch (error) {
      console.error("Failed to add customer:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      setOpen(newOpen);
      if (!newOpen) {
        setIsSubmitting(false);
      }
    }}>
      <DialogTrigger asChild>
        <Button size="sm" className="flex items-center gap-2 text-xs md:text-sm px-3 py-2 md:px-4 md:py-2">
          <Plus className="h-3 w-3 md:h-4 md:w-4" />
          <span className="hidden sm:inline">زیادکردنی کڕیار</span>
          <span className="sm:hidden">کڕیار</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] flex flex-col" dir="rtl">
        <DialogHeader className="text-right">
          <DialogTitle className="text-right">زیادکردنی کڕیاری نوێ</DialogTitle>
          <DialogDescription className="text-right">زانیاریەکانی کڕیار پڕ بکەوە</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto space-y-4 pb-4">
            {/* کۆد و ناو */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="customerCode">کۆدی کڕیار *</Label>
                <Input
                  id="customerCode"
                  type="number"
                  value={formData.customerCode}
                  onChange={(e) => handleCodeChange(e.target.value)}
                  required
                  placeholder="کۆد"
                  dir="ltr"
                  className="text-center h-10"
                />
                {codeError && <p className="text-sm text-destructive text-center">{codeError}</p>}
              </div>
              <div className="space-y-2 md:col-span-3">
                <Label htmlFor="fullName">ناوی تەواو *</Label>
                <Input
                  id="fullName"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  required
                  placeholder="ناوی تەواو"
                  className="h-10"
                />
              </div>
            </div>

            {/* پیشە */}
            <div className="space-y-2">
              <Label>پیشە</Label>
              <DialogSelect
                value={formData.job}
                onChange={(value) => setFormData({ ...formData, job: value })}
                options={jobOptions}
                placeholder="هەڵبژێرە"
                searchable={true}
                searchPlaceholder="گەڕان..."
                emptyMessage={jobs.length === 0 ? "هیچ پیشەیەک لە داتابەیس نییە" : "هیچ پیشەیەک نەدۆزرایەوە"}
                allowClear={true}
                clearLabel="هیچی"
              />
            </div>

            {/* ژمارە مۆبایلەکان */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="phone1">ژمارە مۆبایل ١ *</Label>
                <Input
                  id="phone1"
                  value={formData.phone1}
                  onChange={(e) => setFormData({ ...formData, phone1: e.target.value })}
                  required
                  placeholder="07xxxxxxxxx"
                  dir="ltr"
                  className="text-center"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone2">ژمارە مۆبایل ٢</Label>
                <Input
                  id="phone2"
                  value={formData.phone2}
                  onChange={(e) => setFormData({ ...formData, phone2: e.target.value })}
                  placeholder="07xxxxxxxxx"
                  dir="ltr"
                  className="text-center"
                />
              </div>
            </div>

            {/* زانیاریەکانی کەفیل */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="guarantorName">ناوی کەفیل</Label>
                <Input
                  id="guarantorName"
                  value={formData.guarantorName}
                  onChange={(e) => setFormData({ ...formData, guarantorName: e.target.value })}
                  placeholder="ناوی کەفیل"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="guarantorPhone">ژمارەی کەفیل</Label>
                <Input
                  id="guarantorPhone"
                  value={formData.guarantorPhone}
                  onChange={(e) => setFormData({ ...formData, guarantorPhone: e.target.value })}
                  placeholder="07xxxxxxxxx"
                  dir="ltr"
                  className="text-center"
                />
              </div>
            </div>

            {/* ناونیشان */}
            <div className="space-y-2">
              <Label htmlFor="address">ناونیشان</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="ناونیشان"
              />
            </div>

            {/* تێبینی */}
            <div className="space-y-2">
              <Label htmlFor="notes">تێبینی</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="تێبینی..."
                rows={2}
                className="resize-none"
              />
            </div>
          </div>

          {/* دوگمەکان */}
          <div className="flex flex-row gap-2 pt-4 border-t bg-background sticky bottom-0">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={isSubmitting} className="flex-1">
              پاشگەزبوونەوە
            </Button>
            <Button type="submit" disabled={isSubmitting} className="flex-1">
              {isSubmitting ? "زیادکردن..." : "زیادکردن"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
